import { ChangeDetectorRef, Component, ElementRef, inject, input, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MarkdownRendererComponent } from '../markdown-renderer/markdown-renderer.component';
import { AiToolData, ChatMessage, ChatService, MessageAuthor } from '../service/chat.service';
import { McpAdapterService } from '../service/mcp-adapter.service';

@Component({
    selector: 'app-ai-chat',
    standalone: true,
    imports: [
        DatePipe,
        MarkdownRendererComponent
    ],
    templateUrl: './ai-chat.component.html',
    styleUrl: './ai-chat.component.scss'
})
export class AiChatComponent implements OnInit, OnDestroy {

    public conversationId = input<string>(crypto.randomUUID());
    public isLoading = signal<boolean>(false);
    private abortController: AbortController | undefined;

    public chatMessages: ChatMessage[] = [];

    public onAppendMessage = output<ChatMessage>();

    private taElement = viewChild<ElementRef<HTMLTextAreaElement>>('userMessageTextArea');
    private scrollTimeout: ReturnType<typeof setTimeout> | null = null;
    private readonly chatService = inject(ChatService);
    protected readonly mcpAdapter = inject(McpAdapterService);
    private cdr = inject(ChangeDetectorRef);

    private componentId = crypto.randomUUID().replaceAll('-', '');
    private toggleFuncName = `toggleHide_${this.componentId}`;

    constructor() {
        this.chatMessages.push({
            id: 0,
            sequence: 0,
            timestamp: new Date(),
            conversationId: this.conversationId(),
            author: 'system',
            message: `In this conversation work with the Excel introspection MCP fileId ${this.mcpAdapter.sessionId}`
        });

        this.addToggleHiddenJs();
    }

    private addToggleHiddenJs() {
        (window as any)[this.toggleFuncName] = this.toggleHiddenCls.bind(this);
    }

    ngOnDestroy(): void {
        delete (window as any)[this.toggleFuncName];
    }

    private toggleHiddenCls(event: Event) {
        (event.target as HTMLElement).classList.toggle('hidden');
    }

    private get userTextArea() {
        return this.taElement()!.nativeElement;
    }

    private appendMessage(author: MessageAuthor, message: string): ChatMessage {
        const messageObj: ChatMessage = {
            id: undefined,
            conversationId: this.conversationId(),
            author,
            message,
            sequence: this.chatMessages.length + 1,
            timestamp: new Date()
        };
        this.chatMessages.push(messageObj);
        return messageObj;
    }

    private publishMessage(message: ChatMessage | undefined) {
        if (message) {
            this.onAppendMessage.emit(message);
        }
    }

    private scrollToBottom() {
        if (this.scrollTimeout == null) {
            this.scrollTimeout = setTimeout(() => {
                this.scrollTimeout = null;
                this.userTextArea.scrollIntoView(false);
            }, 50);
        }
    }

    protected async streamChatMessages(msg: string) {
        const chatHistory = this.chatMessages.map(m => ({ role: m.author, content: m.message }));
        const assistantResponse = this.appendMessage('assistant', '');

        try {
            this.abortController = new AbortController();
            this.isLoading.set(true);
            await this.chatService.streamMessages(
                this.conversationId(), chatHistory, msg,
                this.onAddChatMessageChunk.bind(this, assistantResponse),
                this.onAddToolCall.bind(this, assistantResponse),
                this.abortController.signal
            );
        } catch (err) {
            console.error('Error in chat message streaming', err);
        } finally {
            this.abortController = undefined;
            this.isLoading.set(false);
            this.publishMessage(assistantResponse);
        }
    }

    private onAddChatMessageChunk(assistantResponseBlock: ChatMessage, chatMessageChunk: string) {
        assistantResponseBlock.message += chatMessageChunk;
        this.cdr.markForCheck();
        this.scrollToBottom();
    }

    private onAddToolCall(assistantResponseBlock: ChatMessage, toolData : AiToolData) {
        const callArgs: string[] = [];
        for (const argName in toolData.input) {
            const argValue = toolData.input[argName];
            callArgs.push(`${argName}: ${argValue}`);
        }
        const callArgsText = callArgs.join(', ');
        const callResultText = (toolData.output) ? JSON.stringify(toolData.output) : '<void/null>';
        assistantResponseBlock.message += `<pre class="ai_tool_call hidden" onclick="${this.toggleFuncName}(event)">${toolData.name}(${callArgsText})`
            + `-> ${callResultText}</pre>\n`;
    }

    async ngOnInit() {
    }

    async onAutoResize(event: KeyboardEvent) {
        const el = this.userTextArea;
        if (!el) return;
        if (event.key === 'Enter') {
            await this.onSubmitUserMessage();
        } else {
            const scrollHeight = el.scrollHeight;
            const currentHeight = el.offsetHeight ;
            const presetHeight = 0;
            const newHeight = Math.max(Math.max(scrollHeight, currentHeight), presetHeight);
            el.style.height = `${newHeight}px`;
        }
    }

    protected async onSubmitUserMessage() {
        const msgText = this.userTextArea.value?.trim();
        if (!msgText) return;

        const msg = this.appendMessage('user', msgText);
        setTimeout(() => {
            this.userTextArea.value = '';
            this.scrollToBottom();
        });
        this.publishMessage(msg);
        await this.streamChatMessages(msgText);
    }

    protected onInterruptChat() {
        this.abortController?.abort();
    }
}
